import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase as typedSupabase } from '../lib/supabase';

// app_notifications is newer than the generated database types
const supabase = typedSupabase as unknown as SupabaseClient;

export interface PaymentDetails {
  invoice_number: string | null;
  /** Without VAT. */
  amount: number;
  /** Client invoices of the campaign still to be paid. */
  open_invoices: number;
  partial: boolean;
}

export interface AppNotification {
  id: string;
  type: 'invoice_request' | 'payment_received' | (string & {});
  title: string;
  body: string | null;
  campaign_id: string | null;
  projecto_id: string | null;
  invoice_request_id: string | null;
  details: PaymentDetails | null;
  read_at: string | null;
  created_at: string;
}

export const appNotificationService = {
  async list(limit = 30): Promise<AppNotification[]> {
    const { data, error } = await supabase
      .from('app_notifications')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw error;
    return (data || []) as AppNotification[];
  },

  async markRead(ids: string[]): Promise<void> {
    if (ids.length === 0) return;
    const { error } = await supabase
      .from('app_notifications')
      .update({ read_at: new Date().toISOString() })
      .in('id', ids)
      .is('read_at', null);
    if (error) throw error;
  },

  /** Calls back as soon as a notification arrives. Returns how to stop listening. */
  subscribeToNew(userId: string, callback: (notification: AppNotification) => void): () => void {
    const channel = supabase
      .channel(`app-notifications:${userId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'app_notifications', filter: `user_id=eq.${userId}` },
        payload => callback(payload.new as AppNotification),
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  },
};
